// src/components/onboarding/StepIdentite.js
import React, { useState } from 'react';
import { User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FAMILLES, GENDERS } from '../../constants';
import { validateDateOfBirth } from '../../utils';
import { Input, Infobulle } from '../ui';

const StepIdentite = ({ data, onChange, errors }) => {
  const { t } = useTranslation();
  const [clanSearch, setClanSearch] = useState('');

  const filteredClans = clanSearch
    ? FAMILLES.filter((c) => c.toLowerCase().includes(clanSearch.toLowerCase()))
    : FAMILLES;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-3">
          <User size={28} />
        </div>
        <h2 className="text-lg sm:text-xl font-bold dark:text-gray-100">
          {t('stepIdentite.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          {t('stepIdentite.subtitle')}
        </p>
      </div>

      <Input
        label={t('stepIdentite.nameLabel')}
        value={data.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={t('stepIdentite.namePlaceholder')}
        error={errors.name}
        maxLength={50}
      />

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {t('stepIdentite.dobLabel')}
        </label>
        <input
          type="date"
          value={data.dateOfBirth}
          onChange={(e) => onChange({ dateOfBirth: e.target.value })}
          className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 ${
            errors.dateOfBirth
              ? 'border-red-300'
              : 'border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500'
          } rounded-xl dark:text-gray-100 focus:outline-none transition`}
        />
        {data.dateOfBirth && !errors.dateOfBirth && (
          <p className="text-emerald-600 dark:text-emerald-400 text-xs mt-1">
            {t('stepIdentite.ageDisplay', { age: validateDateOfBirth(data.dateOfBirth).age })}
          </p>
        )}
        {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {t('stepIdentite.genderLabel')}
        </label>
        <div className="flex gap-2">
          {GENDERS.map((g) => (
            <button
              key={g.value}
              type="button"
              onClick={() => onChange({ gender: g.value })}
              className={`flex-1 py-3 rounded-2xl font-semibold text-sm transition-all ${
                data.gender === g.value
                  ? 'bg-rose-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {t(g.labelKey)}
            </button>
          ))}
        </div>
        {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {t('stepIdentite.clanLabel')}{' '}
          <span className="text-gray-400 font-normal">{t('stepIdentite.clanOptional')}</span>
        </label>
        <Infobulle type="info">{t('stepIdentite.clanInfo')}</Infobulle>
        <input
          type="text"
          value={clanSearch}
          onChange={(e) => setClanSearch(e.target.value)}
          placeholder={t('stepIdentite.clanSearchPlaceholder')}
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition text-sm mb-2"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
          {filteredClans.map((clan) => (
            <button
              key={clan}
              type="button"
              onClick={() => {
                onChange({ clan });
                setClanSearch('');
              }}
              className={`py-2 px-2 rounded-xl text-sm font-medium transition-all ${
                data.clan === clan
                  ? 'bg-rose-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {clan === 'Autre' ? t('constants.familles.autre') : clan}
            </button>
          ))}
        </div>
        {errors.clan && <p className="text-red-500 text-sm mt-1">{errors.clan}</p>}
      </div>
    </div>
  );
};

export default StepIdentite;
